import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import {numberFormatter} from '../../../utils/utils';

// Define the props for the BarChart component
interface BarChartProps {
  title: string;
  categories: string[];
  data: number[];
  yAxisLabel: string;
  xAxisLabel: string;
  height?: number;
  width?: number;
  color?: string;
  dataTestId?: string;
}

const BarChart: React.FC<BarChartProps> = ({
  title,
  categories,
  data,
  yAxisLabel,
  xAxisLabel,
  height = 500,
  width = 512,
  dataTestId,
}) => {
  // Find the maximum value in the data array
  const maxDataValue = Math.max(...data);

  // Calculate tick positions dynamically at 0%, 25%, 50%, 75%, and 100% of max value
  const tickPositionsDynamic = [
    0,
    Math.ceil(maxDataValue * 0.25),
    Math.ceil(maxDataValue * 0.5),
    Math.ceil(maxDataValue * 0.75),
    maxDataValue,
  ];

  const options: Highcharts.Options = {
    chart: {
      type: 'bar',
      backgroundColor: '#0C1119',
      marginRight: 50,
      height: categories.length > 10 ? categories.length * 50 : 500, // Maintain bar height, adjust chart height
      borderRadius: 12,
    },
    title: {
      text: title,
      align: 'left',
      style: {color: '#F2F4F7', fontSize: '16px'},
    },
    xAxis: {
      categories: categories,
      title: {
        text: xAxisLabel,
        style: {
          color: '#F2F4F7',
        },
      },
      labels: {
        style: {
          fontSize: '12px',
          color: '#F2F4F7',
        },
      },
    },
    yAxis: {
      title: {
        text: yAxisLabel,
        style: {
          color: '#F2F4F7',
        },
      },
      min: 0,
      max: maxDataValue,
      tickPositions: tickPositionsDynamic,
      labels: {
        overflow: 'justify',
        style: {
          fontSize: '12px',
          color: '#F2F4F7',
        },
      },
      gridLineColor: '#4d4d4d',
    },
    tooltip: {
      formatter: function () {
        return `${this.x}<br/>
        <span style="color:${this.color}">\u25CF</span> ${this.series.name}: <b>${numberFormatter(Number(this.y ?? 0))} Docs</b>`;
      },
    },
    plotOptions: {
      bar: {
        dataLabels: {
          enabled: true,
          color: '#FCFCFD',
          crop: false,
          overflow: 'allow',
          align: 'left',
          verticalAlign: 'middle',
          y: -1,
          x: 0,
          formatter: function () {
            return numberFormatter(Number(this.y ?? 0));
          },
        },
      },
    },
    legend: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    series: [
      {
        name: 'Dataset',
        data: data,
        color: '#6E87FF',
        borderColor: '6E87FF',
        type: 'bar',
      },
    ],
  };

  return (
    <div
      style={{
        overflowY: categories.length > 10 ? 'auto' : 'visible', // Enable scrollbar if more than 10 categories
        maxHeight: `${height}px`, // Restrict the height to avoid overflowing
        maxWidth: `${width}px`,
        marginLeft: '0',
        textAlign: 'left',
      }}
      data-testid={dataTestId}
      className="dataset-summary-container"
    >
      <HighchartsReact highcharts={Highcharts} options={options} />
    </div>
  );
};

export default BarChart;
